import { loadStudioDeckTypes, loadStudioScenarios } from 'src/components/star-grid/services/studioOptionService';
import { prepareStudioTemplate } from 'src/components/star-grid/utils/studioContext';
import { scratchStudioTemplate } from 'src/components/star-grid/utils/studioScratchTemplate';
import { listSelectablePnlHierarchies } from 'src/components/star-grid/services/pnlDimensionService';
import { withSelectableStudioBindings } from 'src/components/star-grid/utils/studioBindings';
import type { StudioCatalog, StudioTemplate } from 'src/components/star-grid/reportStudio.types';
import type { ReportSpec } from 'src/components/star-grid/api/reportEngineTypes';
import { STUDIO_CATALOG_PATH, STUDIO_REPORT_PATH, STUDIO_TEMPLATE_PATH } from 'src/components/star-grid/reportStudioConstants';
import { apiRequest } from 'src/components/star-grid/services/starGridHttp';
import { getReport } from 'src/components/star-grid/api/reportEngineClient';
import { parseStudioCatalog, parseStudioDefinition } from 'src/components/star-grid/utils/studioSchema';
export const loadStudioCatalog = async (signal?: AbortSignal): Promise<StudioCatalog> => {
  const [catalog, groups, deckTypes, scenarios] = await Promise.all([
    apiRequest(STUDIO_CATALOG_PATH, undefined, signal).then(parseStudioCatalog),
    listSelectablePnlHierarchies(signal),
    loadStudioDeckTypes(signal),
    loadStudioScenarios(signal)
  ]);
  return withSelectableStudioBindings({ ...catalog, deck_types: deckTypes, scenarios: scenarios ?? catalog.scenarios }, groups);
};
export const loadStudioTemplate = async (id: string, catalog: StudioCatalog, signal: AbortSignal): Promise<StudioTemplate> => {
  const definition = await parseStudioDefinition(await apiRequest(`${STUDIO_TEMPLATE_PATH}/${encodeURIComponent(id)}`, undefined, signal));
  const template = catalog.templates.find((item) => item.id === id);
  if (definition.template_id !== id || definition.template_version !== template?.definition_version || definition.dataset_id !== template.dataset_id)
    throw new Error('studioTemplateChanged');
  const dataset = catalog.datasets.find((item) => item.id === definition.dataset_id);
  if (
    !dataset ||
    Object.values(definition.decks).some((deck) =>
      [...deck.rows, ...deck.filters].some((field) => !dataset.bindings.some((binding) => binding.id === field.binding_id))
    )
  )
    throw new Error('studioTemplateChanged');
  return prepareStudioTemplate({ definition, catalog, parameters: [] });
};
/**
 * Reopens a saved report as the same `StudioTemplate` the create flow edits, carrying `report_id` so a
 * save updates that report instead of creating another.
 */
export const loadStudioReport = async (reportId: number, catalog: StudioCatalog, signal: AbortSignal): Promise<StudioTemplate> => {
  const spec = await getReport(String(reportId), signal);
  // A report the Studio saves is a spec (an old template-built row comes back ADAPTED): the editor loads it by `editingReportId` and saves it back in
  // place, so this only supplies the shelves -- the catalog template the report was built from.
  if (!spec.adapted_provenance) {
    const saved = { report_id: reportId, report_name: spec.name ?? '', report_description: '' };
    const template = spec.dataset ? undefined : templateForSpec(spec, catalog);
    if (template) {
      try {
        return { ...(await loadStudioTemplate(template.id, catalog, signal)), ...saved };
      } catch (error) {
        if (signal.aborted) throw error;
      }
    }
    // No usable template: the shelves come from the report's own dataset, so a saved report always opens.
    return { ...specTemplate(spec, catalog), ...saved };
  }
  return loadTemplateBuiltReport(reportId, catalog, signal);
};

/** The from-scratch template on the spec's own dataset and decks; the editor fills the decks from the spec. */
const specTemplate = (spec: ReportSpec, catalog: StudioCatalog): StudioTemplate => {
  const datasetId = spec.dataset?.id ?? datasetIdForCube(spec.cube_source ?? '');
  if (!catalog.datasets.some((item) => item.id === datasetId)) throw new Error('studioTemplateChanged');
  const ownDeck = spec.view_controls?.find((control) => control.id === 'deck')?.value;
  const decks = [...new Set([ownDeck, ...Object.keys(spec.deck_overrides ?? {})].filter((deck): deck is string => Boolean(deck)))];
  return scratchStudioTemplate(catalog, datasetId, decks);
};

/** The service's dataset id for a cube (`FPA Deck` -> `fpa_deck`). */
const datasetIdForCube = (cube: string): string =>
  cube
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

/**
 * The catalog template a spec was built from, matched on the screen name it records. A spec saved from a
 * template the catalog no longer lists (the old generic `D&S P&L`), or one not ready to run, opens on a ready template of its own
 * dataset: the template only supplies the shelves, the report's rows and columns come from its spec.
 */
const templateForSpec = (spec: { template_label?: string | null; cube_source?: string }, catalog: StudioCatalog) => {
  const label = (spec.template_label ?? '').trim().toLowerCase();
  const dataset = datasetIdForCube(spec.cube_source ?? '');
  return (
    catalog.templates.find((item) => item.ready && (item.label ?? '').trim().toLowerCase() === label) ??
    catalog.templates.find((item) => item.ready && item.dataset_id === dataset)
  );
};

/** A report saved by the template editor, reopened through its template definition. */
const loadTemplateBuiltReport = async (reportId: number, catalog: StudioCatalog, signal: AbortSignal): Promise<StudioTemplate> => {
  const raw = await apiRequest(`${STUDIO_REPORT_PATH}/${reportId}`, undefined, signal);
  const definition = await parseStudioDefinition(raw);
  const dataset = catalog.datasets.find((item) => item.id === definition.dataset_id);
  if (!dataset) throw new Error('studioTemplateChanged');
  const echoed = raw as { report_id?: number; report_name?: string; report_description?: string | null };
  if (echoed.report_id !== reportId) throw new Error('studioPreviewMismatch');
  return {
    ...prepareStudioTemplate({ definition, catalog, parameters: [] }),
    report_id: reportId,
    report_name: echoed.report_name ?? '',
    report_description: echoed.report_description ?? ''
  };
};
